import type { Theme } from '@mui/material/styles';
import type { ApexOptions } from 'apexcharts';
import { formatChartTooltipValue, formatChartValue } from '@/componentsv2/charts/chartOptions';
import type { DashboardMetrics, DashboardPctKey, DashboardSeriesKey } from '@/types/dashboard';
import { addDays, dayKeyToSyntheticUtcMs, isValidTimeZone, orgDayKey, orgDayStartMs, parseDayKey, type DayKey } from '@/utils/orgCalendar';

const HOUR_MS = 60 * 60 * 1000;
const HOURLY_AXIS_LABEL_STEP = 6;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

export type SalesPerformanceChartData = {
  series: Array<{ data: number[] }>;
  categories: Array<string | number>;
  categoryType: 'category' | 'datetime';
  // Sparse axis text parallel to categories; tooltips keep the full category.
  axisLabels?: string[];
  pct: number | null;
};

export type SalesPerformanceCharts = Partial<Record<DashboardSeriesKey, SalesPerformanceChartData>>;

type ChartAxis = Pick<SalesPerformanceChartData, 'categories' | 'axisLabels'>;

type ChartDefinition = {
  seriesKey: DashboardSeriesKey;
  pctKey: DashboardPctKey;
  categoryType: SalesPerformanceChartData['categoryType'];
  axis: (length: number, reportDay: DayKey | null, timezone: string) => ChartAxis | null;
};

const fixedCategories = (values: readonly string[], length: number): ChartAxis => ({
  categories: Array.from({ length }, (_, index) => values[index] ?? String(index + 1)),
});

// Axis text marks only the first occurrence of each clock hour on the step, so DST days keep clock-aligned anchors.
const hourlyAxis = (length: number, reportDay: DayKey | null, timezone: string): ChartAxis | null => {
  if (!reportDay) return null;
  const dayStart = orgDayStartMs(reportDay, timezone);
  const labelFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    hour12: true,
    timeZone: timezone,
  });
  const clockHourFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    hourCycle: 'h23',
    timeZone: timezone,
  });
  const labeledHours = new Set<number>();
  const categories: string[] = [];
  const axisLabels: string[] = [];

  for (let index = 0; index < length; index += 1) {
    const instant = new Date(dayStart + index * HOUR_MS);
    const label = labelFormatter.format(instant);
    const clockHour = Number(clockHourFormatter.format(instant));
    const isAnchor = clockHour % HOURLY_AXIS_LABEL_STEP === 0 && !labeledHours.has(clockHour);
    if (isAnchor) labeledHours.add(clockHour);
    categories.push(label);
    axisLabels.push(isAnchor ? label : '');
  }

  return { categories, axisLabels };
};

const monthToDateAxis = (length: number, reportDay: DayKey | null): ChartAxis | null => {
  if (!reportDay) return null;
  const monthStart = parseDayKey(`${reportDay.slice(0, 7)}-01`);
  if (!monthStart) return null;
  return { categories: Array.from({ length }, (_, index) => dayKeyToSyntheticUtcMs(addDays(monthStart, index))) };
};

const DEFINITIONS: readonly ChartDefinition[] = [
  {
    seriesKey: 'sales_today_by_hour',
    pctKey: 'sales_today_pct_change',
    categoryType: 'category',
    axis: hourlyAxis,
  },
  {
    seriesKey: 'sales_wtd_by_day',
    pctKey: 'sales_wtd_pct_change',
    categoryType: 'category',
    axis: (length) => fixedCategories(WEEKDAYS, length),
  },
  {
    seriesKey: 'sales_mtd_by_day',
    pctKey: 'sales_mtd_pct_change',
    categoryType: 'datetime',
    axis: monthToDateAxis,
  },
  {
    seriesKey: 'sales_ytd_by_month',
    pctKey: 'sales_ytd_pct_change',
    categoryType: 'category',
    axis: (length) => fixedCategories(MONTHS, length),
  },
];

export const buildSalesPerformanceCharts = (metrics: DashboardMetrics, timezone: string): SalesPerformanceCharts => {
  const reportDay = typeof metrics.time_stamp === 'string' && isValidTimeZone(timezone) ? orgDayKey(metrics.time_stamp, timezone) : null;
  const charts: SalesPerformanceCharts = {};

  for (const definition of DEFINITIONS) {
    const values = metrics[definition.seriesKey];
    if (!Array.isArray(values)) continue;
    const axis = definition.axis(values.length, reportDay, timezone);
    if (!axis) continue;
    charts[definition.seriesKey] = {
      series: [{ data: [...values] }],
      ...axis,
      categoryType: definition.categoryType,
      pct: metrics[definition.pctKey] ?? null,
    };
  }

  return charts;
};

export const salesPerformanceChartOptions = (theme: Theme, chartId: string, data: SalesPerformanceChartData | undefined): ApexOptions => ({
  chart: {
    id: `basic-bar-${chartId}`,
    background: 'transparent',
    foreColor: theme.palette.text.primary,
    zoom: { enabled: false },
  },
  dataLabels: { enabled: false },
  grid: {
    borderColor: theme.palette.divider,
    xaxis: { lines: { show: true } },
    yaxis: { lines: { show: true } },
  },
  theme: { mode: theme.palette.mode },
  tooltip: {
    y: { formatter: (value: number): string => formatChartTooltipValue(value, 'currency') },
  },
  xaxis: {
    categories: data?.categories ?? [],
    ...(data?.axisLabels ? { overwriteCategories: data.axisLabels } : {}),
    labels: {
      datetimeUTC: true,
      // Sparse anchors fit horizontally even though Apex sizes rotation from every category.
      ...(data?.axisLabels ? { rotate: 0 } : {}),
    },
    tickPlacement: 'between',
    type: data?.categoryType,
  },
  yaxis: {
    labels: {
      formatter: (value: number): string => formatChartValue(value, 'currency'),
    },
  },
});
